import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Typography, Tooltip, IconButton, Button, useMediaQuery } from '@material-ui/core';
import RemoveIcon from '@material-ui/icons/Clear';
import EditIcon from '@material-ui/icons/Edit';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import DeleteIcon from '@material-ui/icons/Delete';

// CONSTANTS
import { ROUTE_PROFILE_SUBSCRIPTION_DETAIL, ROUTE_DISCOVER_APP } from '@/constants/routes';
import { LOCATIONS_MY_APPS_HAVE_PAGE } from '@/constants/locations';

// UTILITY
import { getLink } from '@/utility/routes';
import { updateUrl } from '@/utility/common';
import { externalLink, getExternalUrlParams } from '@/utility/analytics';

// COMPONENTS
import RouterLink from '@/components/Link';
import Loader from '@/components/Loader';
import DialogMenuActions from '@/components/Dialogs/DialogMenuActions';
import MBIcon from '@/components/Icons/MBIcon';

// STYLES
import { AppCardContainer } from './styles';

function getRightIconData(
  { user_subscribed, service_account_created, concierge_status },
  { removeApp, openDialog },
  isMobile,
) {
  const isSubscribed = user_subscribed;
  const conciergeStatus = concierge_status;
  const isIncompletedRegistration = !service_account_created;

  const iconData = {
    component: 'button',
    icon: <RemoveIcon fontSize="large" className="remove-icon" />,
    title: 'buttons.remove',
    onClick: removeApp,
  };

  if (conciergeStatus) return null;

  if (isSubscribed) {
    return isIncompletedRegistration
      ? {
          component: 'div',
          icon: <ErrorOutlineIcon fontSize="large" color="error" />,
          title: 'subscription.notifications.required_registration',
        }
      : null;
  }

  if (isMobile) {
    return {
      component: 'button',
      icon: <MoreHorizIcon fontSize="large" className="more-icon" />,
      title: 'buttons.more',
      onClick: openDialog,
    };
  }

  return iconData;
}

function getLeftIconData({ user_subscribed }) {
  const isSubscribed = user_subscribed;

  const iconData = null;

  if (isSubscribed) {
    return {
      component: 'div',
      icon: <MBIcon fontSize="large" color="primary" className="mb-icon" />,
      title: 'subscription.notifications.purchased_mybundle',
    };
  }

  return iconData;
}

function getPriceTransKey(price) {
  return price && Math.ceil(price) !== 0 ? `$${price}` : 'common.free';
}

function getPriceWithMonthTransKey(price) {
  return `common.${price && Math.ceil(price) !== 0 ? 'costMonth' : 'free'}`;
}

function getWantPrice(t, data, isMobile) {
  return data.deal_price || data.deal_price === 0 ? (
    <div className="app-card__deal-price">
      <Typography className="p4 app-card__cost--deal" component="span" color="textSecondary">
        {t(getPriceWithMonthTransKey(data.deal_price), { cost: data.deal_price })}
      </Typography>
      <Typography
        className="p4 app-card__cost--current"
        component="span"
        color={isMobile ? 'textSecondary' : 'textPrimary'}
      >
        {t(getPriceWithMonthTransKey(data.price), { cost: data.price })}
      </Typography>
    </div>
  ) : (
    <Typography className="p4" component="span" color={isMobile ? 'textSecondary' : 'textPrimary'}>
      {t(getPriceWithMonthTransKey(data.price), { cost: data.price })}
    </Typography>
  );
}

const AppCardSecondary = props => {
  const {
    data,
    partner,
    fullHeight,
    removeAppListStatus,
    statusesLoading,
    listType,
    unique_id,
    changePriceClick,
    withShadow,
  } = props;
  const [openDialog, setOpenDialog] = useState(false);
  const [t] = useTranslation();
  const matchesUpSm = useMediaQuery(({ breakpoints }) => breakpoints.up('sm'));
  const isMobile = !matchesUpSm;
  const hasPartner = !!partner.slug;
  const partnerSettings = partner.settings || {};
  let useSubscription = true;

  if (hasPartner) {
    useSubscription = !!partnerSettings.use_authorizing && !!partnerSettings.use_subscription;
  }

  const externalUrlParams = getExternalUrlParams({ unique_id });
  const mainPrice = data.user_price ?? data.price;
  const isSubscribed = data.user_subscribed;
  const conciergeStatus = data?.concierge_status;
  const isHaveListType = listType === 'have';
  const isWantListType = listType === 'want';
  const showEditPrice = isHaveListType && !isSubscribed && !isMobile && !conciergeStatus;

  function removeApp() {
    removeAppListStatus(data);
  }

  function openChangePriceDialog() {
    changePriceClick(true, data);
  }

  function onCloseAppMoreDialog() {
    setOpenDialog(false);
  }

  const rightIconData = getRightIconData(
    data,
    {
      removeApp,
      openDialog: () => setOpenDialog(true),
    },
    isMobile,
  );
  const leftIconData = getLeftIconData(data, isMobile);

  const visitSiteUrl = updateUrl(data.url, externalUrlParams);
  const routeLinkLocation = LOCATIONS_MY_APPS_HAVE_PAGE;

  const appLink = getLink(
    isHaveListType && useSubscription ? ROUTE_PROFILE_SUBSCRIPTION_DETAIL : ROUTE_DISCOVER_APP,
    {
      slug: data.unique_slug,
    },
  );

  function handleVisitSite() {
    const properties = {
      location: 'app page',
      href: visitSiteUrl,
      app_id: data.id,
      type: 'app',
      service_name: data.name,
      is_affiliate: !!data.is_affiliate,
      partner: partner.partner_slug,
      partner_id: partner.partner_id,
    };

    externalLink(properties);
  }

  const tooltipPopperProps = {
    style: {
      maxWidth: 280,
    },
  };

  const actions = [
    {
      icon: <EditIcon fontSize="inherit" />,
      title: t('buttons.editPrice'),
      onClick: openChangePriceDialog,
    },
    {
      icon: <DeleteIcon fontSize="inherit" />,
      title: t('buttons.removeApp'),
      onClick: removeApp,
    },
    {
      icon: null,
      title: t('buttons.close'),
      onClick: onCloseAppMoreDialog,
    },
  ];

  return (
    <>
      <DialogMenuActions
        type="only-dialog"
        open={openDialog}
        onClose={onCloseAppMoreDialog}
        actions={actions}
      />
      <AppCardContainer
        className={clsx('app-card', {
          'app-card--full-height': fullHeight,
          [`app-card__${listType}`]: listType,
        })}
        withShadow={withShadow}
      >
        {isWantListType && (
          <div className="app-card__markers">
            {data.has_super_deal && (
              <span className="marker marker--el marker--static">
                {t('common.deal').toUpperCase()}
              </span>
            )}
          </div>
        )}

        {rightIconData && (
          <Tooltip
            title={<p className="p4">{t(rightIconData.title)}</p>}
            placement="top-end"
            PopperProps={tooltipPopperProps}
          >
            <IconButton
              component={rightIconData.component || 'button'}
              className="app-card__icon-btn app-card__right-btn"
              onClick={rightIconData.onClick}
              color="default"
            >
              {rightIconData.icon}
            </IconButton>
          </Tooltip>
        )}

        {leftIconData && (
          <Tooltip
            title={<p className="p4">{t(leftIconData.title)}</p>}
            placement="top-start"
            PopperProps={tooltipPopperProps}
          >
            <IconButton
              component={leftIconData.component || 'button'}
              onClick={leftIconData.onClick}
              className="app-card__icon-btn app-card__left-btn"
            >
              {leftIconData.icon}
            </IconButton>
          </Tooltip>
        )}

        <div className="app-card__link-wrapper">
          <RouterLink to={appLink} location={routeLinkLocation}>
            <div className="app-card__logo-wrapper">
              <div className="app-card__logo">
                <img
                  src={
                    data.thumbnail ||
                    `https://dummyimage.com/200X80/FFFFFF/000000/?text=${data.name}`
                  }
                  alt={data.name}
                />
              </div>
              <div className="app-card__main-info">
                <Typography className="app-card__title" variant="h5" align="center">
                  {data.name}
                </Typography>
              </div>
            </div>
          </RouterLink>
        </div>

        <div className="app-card__cost">
          <Typography
            className="p4 app-card__price"
            color={matchesUpSm ? 'textPrimary' : 'textSecondary'}
          >
            {isWantListType
              ? getWantPrice(t, data, isMobile)
              : t(getPriceTransKey(mainPrice), { cost: mainPrice })}
          </Typography>{' '}
          {showEditPrice && (
            <Tooltip
              PopperProps={tooltipPopperProps}
              title={<p className="p4">{t('profileMyApps.changePrice')}</p>}
              placement="top-start"
            >
              <EditIcon onClick={openChangePriceDialog} />
            </Tooltip>
          )}
        </div>

        {isWantListType && (
          <Button
            className="app-card__button"
            component="a"
            href={visitSiteUrl}
            target="_blank"
            fullWidth
            color="primary"
            onClick={handleVisitSite}
            size="small"
          >
            {data.external_button_text || t('buttons.visitSite')}
          </Button>
        )}
        <Loader active={statusesLoading} />
      </AppCardContainer>
    </>
  );
};

AppCardSecondary.defaultProps = {
  data: {},
  added: false,
  shadow: false,
  hideRating: false,
  hideDescription: false,
  fullHeight: false,
  isAuth: false,
  updateAppListStatus: () => {},
  removeAppListStatus: () => {},
  statusesLoading: false,
  listType: 'have',
  changePriceClick: () => {},
  withShadow: true,
};

AppCardSecondary.propTypes = {
  data: PropTypes.object,
  added: PropTypes.bool,
  shadow: PropTypes.bool,
  partner: PropTypes.object.isRequired,
  hideRating: PropTypes.bool,
  hideDescription: PropTypes.bool,
  fullHeight: PropTypes.bool,
  isAuth: PropTypes.bool,
  updateAppListStatus: PropTypes.func,
  removeAppListStatus: PropTypes.func,
  statusesLoading: PropTypes.bool,
  listType: PropTypes.string,
  unique_id: PropTypes.string.isRequired,
  changePriceClick: PropTypes.func,
  withShadow: PropTypes.bool,
};

export default React.memo(AppCardSecondary);
